import { useEffect, useState } from "react";
import { Search, Loader2, Mail, Phone, UserPlus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

export const CollegaClienteDialog = ({ open, onOpenChange, pratica, onLinked }) => {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setQ(""); setSel(null); } }, [open]);
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const t = setTimeout(() => {
      api.get("/studio/messaggi/clienti", { params: { q } }).then(({ data }) => setItems(data)).catch(() => setItems([])).finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, open]);

  const noChannel = sel && !sel.email && !sel.cellulare;
  const link = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/studio/pratiche/${pratica.id}/collega-cliente`, { anagrafica_id: sel.id });
      toast.success(`${sel.nome} collegato alla pratica ${pratica.numero_pratica}`);
      onLinked(sel, data);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="collega-cliente-dialog">
        <DialogHeader>
          <DialogTitle>Collega cliente</DialogTitle>
          <DialogDescription>Cerca un cliente in anagrafica e collegalo alla pratica {pratica?.numero_pratica}.</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nome, cognome, codice fiscale, email o telefono"
            data-testid="collega-cliente-search" className="w-full h-10 rounded-lg border border-slate-300 pl-8 pr-3 text-sm focus:outline-none focus:border-amber-500" />
        </div>
        <div className="max-h-72 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100" data-testid="collega-cliente-results">
          {loading ? <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-amber-500" /></div>
            : items.length === 0 ? <p className="py-6 text-center text-sm text-slate-500" data-testid="collega-cliente-empty">Nessun cliente trovato.</p>
            : items.map((c) => (
              <button key={c.id} type="button" onClick={() => setSel(c)} data-testid={`collega-cliente-item-${c.id}`}
                className={`w-full text-left px-3 py-2 transition-colors ${sel?.id === c.id ? "bg-amber-50" : "hover:bg-slate-50"}`}>
                <div className="text-sm font-medium text-[#111827]">{c.nome}{c.codice_fiscale && <span className="ml-2 font-mono text-[11px] text-slate-500">{c.codice_fiscale}</span>}</div>
                <div className="flex flex-wrap gap-x-3 text-xs text-slate-600">
                  <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{c.email || "—"}</span>
                  <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{c.cellulare || "—"}</span>
                </div>
              </button>
            ))}
        </div>
        {noChannel && <p className="text-xs text-red-600" role="alert" data-testid="collega-cliente-no-channel">Il cliente selezionato non ha email né cellulare: aggiungili in anagrafica per poter inviare messaggi.</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={() => onOpenChange(false)} data-testid="collega-cliente-cancel"
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-[#111827] hover:bg-slate-50 transition-colors">Annulla</button>
          <button type="button" onClick={link} disabled={!sel || noChannel || busy} data-testid="collega-cliente-confirm"
            className="flex items-center gap-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 px-4 py-2 text-sm font-semibold text-[#0a0d14] transition-colors disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}Collega
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
